import { useEffect, useState } from 'react';
import { getCookieValue } from 'utils/handleCookies';

const ONE_TRUST_COOKIE_NAME = 'OptanonConsent';

type ConsentState = {
  strictlyNecessary: boolean;
  performance: boolean;
  functional: boolean;
  targeting: boolean;
  social: boolean;
};

export const useOneTrustConsent = (): ConsentState => {
  const [consent, setConsent] = useState<ConsentState>({
    // Default to true for all categories. This is to ensure that the script execution areas are always allowed.
    // We only want to block the script execution areas if the user has explicitly opted out.
    strictlyNecessary: true,
    performance: true,
    functional: true,
    targeting: true,
    social: true,
  });

  const parseConsent = () => {
    const optanonConsentCookies = getCookieValue(ONE_TRUST_COOKIE_NAME);

    if (optanonConsentCookies) {
      const consentString = new URLSearchParams(optanonConsentCookies);
      const groupString = consentString.get('groups');

      if (groupString) {
        const groups: Record<string, boolean> = Object.fromEntries(
          groupString.split(',').map((group) => {
            const [id, value] = group.split(':');
            return [id, value === '1'];
          }),
        );

        const getOneTrustConsent = (keys: string[]): boolean => {
          return keys.some((key) => groups[key] === true);
        };

        const oneTrustCategoryKeys = {
          strictlyNecessary: ['C0001', '1'], // always true
          performance: ['C0002', '2'],
          functional: ['C0003', '3'],
          targeting: ['C0004', '4'],
          social: ['C0005', '5'],
        };

        setConsent({
          strictlyNecessary: true, // Group 1 is always allowed
          performance: getOneTrustConsent(oneTrustCategoryKeys.performance),
          functional: getOneTrustConsent(oneTrustCategoryKeys.functional),
          targeting: getOneTrustConsent(oneTrustCategoryKeys.targeting),
          social: getOneTrustConsent(oneTrustCategoryKeys.social),
        });
      }
    }
  };

  useEffect(() => {
    parseConsent();
    window.addEventListener('OneTrustGroupsUpdated', parseConsent);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', parseConsent);
    };
  }, []);

  return consent;
};

export default useOneTrustConsent;
